import { c as _c } from "react/compiler-runtime";
import type { IntlFormatters, IntlShape, MessageDescriptor } from 'react-intl';
import { createIntl, createIntlCache } from 'react-intl';
import en from './en';
import { isSupportedLocaleType, Locales, LocalesType, SupportedLocales, FrontendLocales } from '@commutifi-fe/constants';
import { ConfigContext } from '@commutifi-fe/ui';
import { use, useState, useEffect, useMemo } from 'react';
import { DeepRequired } from 'utility-types';
import { usePrevious } from '@commutifi-fe/hooks';
export type { CustomFormatConfig, CustomFormats, FormatDateOptions, FormatDisplayNameOptions, FormatListOptions, FormatNumberOptions, FormatPluralOptions, FormatRelativeTimeOptions, Formatters, IntlCache, IntlConfig, IntlFormatters, IntlShape, MessageDescriptor, MessageFormatElement, ResolvedIntlConfig, WithIntlProps, WrappedComponentProps } from 'react-intl';
export { en as defaultMessages };
export const DEFAULT_LOCALE = Locales.enUS;
export enum LocalesFilename {
  Cs = 'cs',
  DeDe = 'de-DE',
  En = 'en',
  EsEs = 'es-ES',
  Fr = 'fr',
  ItIt = 'it-IT',
  NlNl = 'nl-NL',
  Pl = 'pl',
  PtPt = 'pt-PT',
}
export const messages = {
  [LocalesFilename.En]: () => new Promise<{
    default: typeof en;
  }>(resolve => {
    resolve({
      default: en
    });
  }),
  [LocalesFilename.Cs]: () => import('./cs'),
  [LocalesFilename.DeDe]: () => import('./de-DE'),
  [LocalesFilename.EsEs]: () => import('./es-ES'),
  [LocalesFilename.Fr]: () => import('./fr'),
  [LocalesFilename.ItIt]: () => import('./it-IT'),
  [LocalesFilename.NlNl]: () => import('./nl-NL'),
  [LocalesFilename.Pl]: () => import('./pl'),
  [LocalesFilename.PtPt]: () => import('./pt-PT')
};
type IntlMessage = typeof en;
export type IntlMessageComponents = keyof IntlMessage;
export type IntlMessageKeys<C extends IntlMessageComponents> = keyof IntlMessage[C];

// The arguments to the original formatMessage function.
type FormatMessageArgs = Parameters<IntlFormatters['formatMessage']>;
export type RecursiveKeyOf<TObj> = { [TKey in keyof TObj & (string | number)]: TObj[TKey] extends any[] ? `${TKey}` : TObj[TKey] extends object ? `${TKey}` | `${TKey}.${RecursiveKeyOf<TObj[TKey]>}` : `${TKey}` }[keyof TObj & (string | number)];

// Define PrimitiveType and FormatXMLElementFn which are used by react-intl but not directly exportable
type PrimitiveType = string | number | boolean | null | undefined;
type FormatXMLElementFn<T, R> = (parts: T[]) => R;

// Modify TypedIntl to be more compatible with IntlShape
export interface TypedIntl<C extends IntlMessageComponents> extends Omit<IntlShape, 'formatMessage'> {
  formatMessage(descriptor: MessageDescriptor & {
    id: RecursiveKeyOf<DeepRequired<IntlMessage>[C] & IntlMessage['global']>;
  }, values?: Record<string, PrimitiveType | FormatXMLElementFn<string, string>>, options?: FormatMessageArgs[2]): string;
}
export const useLocaleCodeFromConfig = () => {
  const {
    localeKey
  } = use(ConfigContext);
  return isSupportedLocaleType(localeKey) ? localeKey : Locales.enUS;
};
export const localeToSupportedTranslationFile = (locale: LocalesType): keyof typeof messages => {
  switch (locale) {
    case SupportedLocales.enCA:
    case SupportedLocales.enGB:
    case SupportedLocales.enUS:
    case SupportedLocales.enAU:
    case FrontendLocales.enVI:
    case FrontendLocales.enTH:
    case FrontendLocales.enPH:
    case FrontendLocales.enAU:
      return LocalesFilename.En;
    case SupportedLocales.cs:
      return LocalesFilename.Cs;
    case SupportedLocales.deDE:
      return LocalesFilename.DeDe;
    case SupportedLocales.esES:
    case FrontendLocales.esCL:
    case FrontendLocales.esCO:
    case FrontendLocales.esMX:
    case FrontendLocales.esUS:
      return LocalesFilename.EsEs;
    case SupportedLocales.frBE:
    case SupportedLocales.frCA:
    case SupportedLocales.frFR:
      return LocalesFilename.Fr;
    case SupportedLocales.itIT:
      return LocalesFilename.ItIt;
    case SupportedLocales.nlBE:
    case SupportedLocales.nlNL:
      return LocalesFilename.NlNl;
    case SupportedLocales.pl:
      return LocalesFilename.Pl;
    case SupportedLocales.ptPT:
      return LocalesFilename.PtPt;
    default:
      return LocalesFilename.En;
  }
};
const intlCache = createIntlCache();
export const useComponentIntl = componentName => {
  const $ = _c(18);
  let t0;
  if ($[0] !== componentName) {
    t0 = [componentName, "global"];
    $[0] = componentName;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const componentNames = t0;
  const locale = useLocaleCodeFromConfig();
  const previousLocale = usePrevious(locale);
  let t1;
  if ($[2] !== componentNames) {
    t1 = componentNames.reduce(_temp, {} as IntlMessage[C]);
    $[2] = componentNames;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const englishMessages = t1;
  const [lazyLoadedMessages, setLazyLoadedMessages] = useState(englishMessages);
  let t2;
  let t3;
  if ($[4] !== componentNames || $[5] !== locale || $[6] !== previousLocale) {
    t2 = () => {
      const intlTranslationsName = localeToSupportedTranslationFile(locale);
      if (!previousLocale && locale === Locales.enUS || locale === previousLocale) {
        return;
      }
      messages[intlTranslationsName]().then(module => {
        setLazyLoadedMessages(componentNames.reduce((acc_0, compName_0) => ({
          ...acc_0,
          ...module.default[compName_0]
        }), {} as IntlMessage[C]));
      }).catch(_temp2);
    };
    t3 = [componentNames, locale, previousLocale];
    $[4] = componentNames;
    $[5] = locale;
    $[6] = previousLocale;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== englishMessages || $[10] !== lazyLoadedMessages || $[11] !== locale) {
    t4 = locale === DEFAULT_LOCALE ? englishMessages : {
      ...englishMessages,
      ...lazyLoadedMessages
    };
    $[9] = englishMessages;
    $[10] = lazyLoadedMessages;
    $[11] = locale;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const messagesWithEnglishAsFallback = t4;
  let t5;
  if ($[13] !== locale || $[14] !== messagesWithEnglishAsFallback) {
    t5 = {
      locale,
      messages: messagesWithEnglishAsFallback
    };
    $[13] = locale;
    $[14] = messagesWithEnglishAsFallback;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const intlData = t5;
  let t6;
  if ($[16] !== intlData) {
    t6 = createIntl(intlData, intlCache);
    $[16] = intlData;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const intl = t6;
  return intl;
};
function _temp(acc, compName) {
  return {
    ...acc,
    ...en[compName]
  };
}
function _temp2() {}